/* eslint-disable react-hooks/exhaustive-deps */
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
} from "@mui/material";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, gql } from "@apollo/client";
import { getCuisines, restaurantList } from "../../apollo/server";
import { resizedImage } from "../../utils/resizedImage";
import { LoginHeader } from "../../components/Header";
import Header from "../../components/Header/Header";
import UserContext from "../../context/User";
import { useLocation } from "../../hooks";
import Analytics from "../../utils/analytics";
import useStyles from "./styles";
import Reveal from "./Reveal";
import RunningLogo from "./RunningLogo";
import HeroBackground3D from "./HeroBackground3D";
import SectionBackground3D from "./SectionBackground3D";
import * as Sentry from "@sentry/react";
import Footer from "../../components/Footer/Footer";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router-dom";
import { useLocationContext } from "../../context/Location";
import Autocomplete from "@mui/material/Autocomplete";
import SyncLoader from "react-spinners/SyncLoader";
import useMapboxAutocomplete from "../../hooks/useMapboxAutocomplete";
// App download — real home-screen shot with the RushMyFood icon
import AppHomeScreen from "../../assets/images/app-homescreen.png";
import Seo from "../../components/Seo/Seo";
import { organizationSchema, webSiteSchema } from "../../utils/seoSchema";
import { FONT_BODY } from "../../utils/designTokens";

// High-quality images - using reliable CDN
const HeroBurger = "https://images.pexels.com/photos/1639562/pexels-photo-1639562.jpeg?auto=compress&cs=tinysrgb&w=800&h=800";
const FoodPlate = "https://images.pexels.com/photos/1146760/pexels-photo-1146760.jpeg?auto=compress&cs=tinysrgb&w=600&h=600";
const DeliveryRider = "https://images.pexels.com/photos/4392036/pexels-photo-4392036.jpeg?auto=compress&cs=tinysrgb&w=800&h=500";
const RestaurantImg = "https://images.pexels.com/photos/958545/pexels-photo-958545.jpeg?auto=compress&cs=tinysrgb&w=800&h=500";
const AboutImg = "https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&cs=tinysrgb&w=800&h=500";
const ScooterImg = "https://images.pexels.com/photos/4393668/pexels-photo-4393668.jpeg?auto=compress&cs=tinysrgb&w=600&h=600";

// "Why Restaurants Love RushMyFood" value cards
const ValueImgVariety = "https://images.pexels.com/photos/696218/pexels-photo-696218.jpeg?auto=compress&cs=tinysrgb&w=600&h=450";
const ValueImgDelivery = "https://images.pexels.com/photos/4393668/pexels-photo-4393668.jpeg?auto=compress&cs=tinysrgb&w=600&h=450";
const ValueImgSave = "https://images.pexels.com/photos/3943716/pexels-photo-3943716.jpeg?auto=compress&cs=tinysrgb&w=600&h=450";
const BadgeAppStore = "https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg";
const BadgeGooglePlay = "https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg";

const GET_CUISINES = gql`
  ${getCuisines}
`;
const RESTAURANTS = gql`
  ${restaurantList}
`;

// Text-only fallback while the real category images load
const CUISINES = [
  "Tacos",
  "Sushi",
  "Biryani",
  "Pizza",
  "Pho",
  "Wings",
  "Burgers",
  "Curry",
  "Poke",
  "BBQ",
  "Falafel",
  "Ramen",
];

const STRIP_PHOTOS = [
  HeroBurger,
  ScooterImg,
  RestaurantImg,
  FoodPlate,
  AboutImg,
  ValueImgVariety,
  ValueImgSave,
];

// Category ticker speed (pixels per second): slow enough to read the names.
const TICKER_PX_PER_SECOND = 30;

function Home() {
  const classes = useStyles();
  const navigate = useNavigate();

  const { getCurrentLocation } = useLocation();
  const [locationError, setLocationError] = useState(null);
  const { isLoggedIn } = useContext(UserContext);
  const { location, setLocation } = useLocationContext();
  const [, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const { options, fetchPredictions } = useMapboxAutocomplete();
  const [gettingLocation, setGettingLocation] = useState(false);
  const heroArtRef = useRef(null);

  // Real cuisine + category images (same ones as "What are you in the mood for?")
  const { data: cuisineData } = useQuery(GET_CUISINES, {
    fetchPolicy: "cache-first",
  });
  const { data: restaurantData } = useQuery(RESTAURANTS, {
    variables: {
      latitude: location?.latitude,
      longitude: location?.longitude,
    },
    skip: !location,
    fetchPolicy: "cache-first",
  });
  const marqueeItems = useMemo(() => {
    const seen = new Set();
    const items = [];
    const push = (id, name, image) => {
      const key = name?.trim().toLowerCase();
      if (!key || !image || seen.has(key)) return;
      seen.add(key);
      items.push({ id, name, image });
    };
    (cuisineData?.getCuisines ?? []).forEach((c) =>
      push(`cuisine-${c._id}`, c.name, c.image)
    );
    (restaurantData?.nearByRestaurants?.restaurants ?? []).forEach((r) =>
      (r.categories ?? []).forEach((cat) =>
        push(`cat-${cat._id}`, cat.title, cat.icon)
      )
    );
    return items.length
      ? items
      : CUISINES.map((name) => ({ id: name, name, image: null }));
  }, [cuisineData, restaurantData]);

  // The ticker moves at the same calm pace however many categories it holds:
  // one full loop takes as long as one copy of the list needs at this speed.
  const tickerGroupRef = useRef(null);
  const [tickerSeconds, setTickerSeconds] = useState(null);
  useEffect(() => {
    const group = tickerGroupRef.current;
    if (!group) return undefined;
    const update = () => {
      const width = group.scrollWidth;
      if (width > 0) setTickerSeconds(Math.max(30, width / TICKER_PX_PER_SECOND));
    };
    update();
    // Icons load after the text; the width grows with them.
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(group);
    return () => observer.disconnect();
  }, [marqueeItems]);

  useEffect(async () => {
    await Analytics.track(Analytics.events.NAVIGATE_TO_HOME);
  }, []);

  useEffect(() => {
    fetchPredictions(inputValue);
  }, [inputValue, fetchPredictions]);

  // Gentle mouse parallax on the hero photo collage
  const handleHeroMouseMove = useCallback((e) => {
    const el = heroArtRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.setProperty("--mx", x.toFixed(3));
    el.style.setProperty("--my", y.toFixed(3));
  }, []);

  const handleHeroMouseLeave = useCallback(() => {
    const el = heroArtRef.current;
    if (!el) return;
    el.style.setProperty("--mx", "0");
    el.style.setProperty("--my", "0");
  }, []);

  const locationCallback = (locError, data) => {
    setGettingLocation(false);
    if (locError) {
      const msg =
        typeof locError === "string"
          ? locError
          : locError?.message ||
            "Location access denied. Please enable location services or type your address.";
      setLocationError(msg);
      return;
    }
    setLocationError(null);
    setLocation(data);
    navigate("/restaurant-list");
  };

  const handleGetCurrentLocation = () => {
    setGettingLocation(true);
    getCurrentLocation(locationCallback);
  };

  const handleFindRestaurants = () => {
    if (location) {
      navigate("/restaurant-list");
    }
  };

  return (
    <Sentry.ErrorBoundary
      fallback={({ error }) => (
        <pre
          style={{
            margin: 20,
            padding: 16,
            border: "2px solid #DC2626",
            borderRadius: 8,
            background: "#FEF2F2",
            color: "#7F1D1D",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: FONT_BODY,
            fontSize: 13,
          }}
        >
          {"⚠️ Home render error:\n\n" +
            String(error?.message) +
            "\n\n" +
            String(error?.stack)}
        </pre>
      )}
    >
      <Box className={classes.root}>
        <Seo
          description="Order food online from your favorite local restaurants. Browse menus, compare delivery times, and get it delivered or ready for pickup with RushMyFood."
          path="/"
          schema={[organizationSchema(), webSiteSchema()]}
        />
        {isLoggedIn ? <Header /> : <LoginHeader showIcon />}

        {/* ---- Hero ---- */}
        <Box
          className={classes.hero}
          onMouseMove={handleHeroMouseMove}
          onMouseLeave={handleHeroMouseLeave}
        >
          <HeroBackground3D />
          <Container maxWidth="lg" className={classes.heroInner}>
            <Box className={classes.heroGrid}>
              <Box className={classes.heroLeft}>
                <Reveal>
                  <Typography component="span" className={classes.eyebrow}>
                    <RunningLogo />
                    Welcome To RushMyFood
                  </Typography>
                </Reveal>
                <Reveal delay={90}>
                  <Typography component="h1" className={classes.heroTitle}>
                    Get Food{" "}
                    <span
                      className={classes.squiggleWrap}
                      style={{ color: "#2E7D32" }}
                    >
                      Delivery
                      <svg
                        className={classes.squiggle}
                        viewBox="0 0 220 24"
                        preserveAspectRatio="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M4 15 C 30 5, 58 21, 90 12 S 152 4, 216 13"
                          pathLength="1"
                        />
                      </svg>
                    </span>
                    <br />
                    <span style={{ color: "#FF3008" }}>and</span> More
                  </Typography>
                </Reveal>
                <Reveal delay={180}>
                  <Typography className={classes.heroSubtitle}>
                    Order from the best local restaurants with easy, on-demand delivery and pickup.
                  </Typography>
                </Reveal>

                <Reveal delay={270}>
                  <Box className={classes.searchBox}>
                    <Autocomplete
                      id="address-search"
                      freeSolo
                      options={options}
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.description
                      }
                      filterOptions={(x) => x}
                      value={location?.deliveryAddress || ""}
                      onChange={(event, newValue) => {
                        if (newValue && newValue.lat != null) {
                          // Extract short label from address (e.g., "New York, NY")
                          const mainText =
                            newValue.structured_formatting?.main_text || "";
                          const shortLabel =
                            mainText ||
                            newValue.description
                              .split(",")
                              .slice(0, 2)
                              .join(",")
                              .trim();
                          setLocation({
                            label: shortLabel,
                            deliveryAddress: newValue.description,
                            latitude: newValue.lat,
                            longitude: newValue.lng,
                          });
                        }
                        setValue(newValue);
                      }}
                      onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                      }}
                      fullWidth
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Enter delivery address"
                          variant="outlined"
                          className={classes.searchInput}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <InputAdornment position="start">
                                <LocationOnIcon sx={{ color: "#FF3008" }} />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <>
                                {params.InputProps.endAdornment}
                                <InputAdornment position="end">
                                  {gettingLocation ? (
                                    <SyncLoader
                                      color="#FF3008"
                                      size={5}
                                      speedMultiplier={0.7}
                                      margin={1}
                                    />
                                  ) : (
                                    <MyLocationIcon
                                      sx={{
                                        color: "#8A7A6D",
                                        cursor: "pointer",
                                        "&:hover": { color: "#FF3008" },
                                      }}
                                      onClick={handleGetCurrentLocation}
                                    />
                                  )}
                                </InputAdornment>
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <LocationOnIcon sx={{ color: "#767676", mr: 1 }} />
                          <Box>
                            <Typography sx={{ fontWeight: 500 }}>
                              {option.structured_formatting?.main_text}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {option.structured_formatting?.secondary_text}
                            </Typography>
                          </Box>
                        </li>
                      )}
                    />
                    <Button
                      variant="contained"
                      className={classes.searchButton}
                      onClick={handleFindRestaurants}
                      disabled={!location}
                    >
                      See what's nearby
                    </Button>
                  </Box>
                </Reveal>

                {gettingLocation && (
                  <Box className={classes.statusPill}>
                    <SyncLoader
                      color="#FF3008"
                      size={5}
                      speedMultiplier={0.7}
                      margin={1}
                    />
                    <Typography className={classes.statusText}>
                      Detecting your location…
                    </Typography>
                  </Box>
                )}
                {locationError && !gettingLocation && (
                  <Box className={classes.statusPill}>
                    <Typography className={classes.statusText}>
                      📍 {locationError}
                    </Typography>
                    <Typography
                      className={classes.statusClose}
                      onClick={() => setLocationError(null)}
                    >
                      ✕
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Photo collage */}
              <Box className={classes.heroArt} ref={heroArtRef}>
                <Box className={classes.parallaxB}>
                  <Box className={`${classes.polaroid} ${classes.polaroidSide}`}>
                    <img
                      src={FoodPlate}
                      alt="Restaurant food"
                      crossOrigin="anonymous"
                    />
                    <span className={classes.polaroidCaption}>
                      fresh off the pass
                    </span>
                  </Box>
                </Box>
                <Box className={classes.parallaxA}>
                  <Box className={`${classes.polaroid} ${classes.polaroidMain}`}>
                    <img
                      src={HeroBurger}
                      alt="Delicious food"
                      crossOrigin="anonymous"
                      loading="eager"
                    />
                    <span className={classes.polaroidCaption}>
                      tonight&apos;s pick 🍔
                    </span>
                  </Box>
                </Box>
                <Box className={classes.parallaxC}>
                  <Box
                    className={`${classes.polaroid} ${classes.polaroidSmall}`}
                  >
                    <img
                      src={ScooterImg}
                      alt="Delivery and pickup"
                      crossOrigin="anonymous"
                    />
                    <span className={classes.polaroidCaption}>on its way!</span>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ---- Category ticker banner ---- */}
        <Box className={classes.marquee}>
          <Box
            className={classes.marqueeTrack}
            style={tickerSeconds ? { animationDuration: `${tickerSeconds}s` } : undefined}
          >
            {[0, 1].map((dup) => (
              <Box component="span" key={dup} ref={dup === 0 ? tickerGroupRef : undefined} className={classes.marqueeGroup}>
                {marqueeItems.map((c) => (
                  <span
                    key={`${dup}-${c.id}`}
                    className={classes.marqueeItem}
                    onClick={() => navigate("/restaurant-list")}
                  >
                    {c.image && (
                      <img
                        src={resizedImage(c.image, 128)}
                        alt=""
                        className={classes.marqueeIcon}
                        loading="lazy"
                      />
                    )}
                    {c.name}
                    <span className={classes.marqueeStar}>✦</span>
                  </span>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ---- Feature Cards ---- */}
        <Box className={classes.section}>
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Box className={classes.featuresGrid}>
              <Reveal style={{ height: "100%" }}>
                <Box className={classes.featureCard}>
                  <Box className={classes.featureCardContent}>
                    <Typography className={classes.featureCardLabel}>
                      Delivery & Pickup
                    </Typography>
                    <Typography className={classes.featureCardTitle}>
                      Order your way
                    </Typography>
                    <Typography className={classes.featureCardDesc}>
                      Get your food delivered to your door or pick it up — whatever works best for you
                    </Typography>
                  </Box>
                  <Box className={classes.featureCardImage}>
                    <img src={ScooterImg} alt="Delivery and pickup" crossOrigin="anonymous" />
                  </Box>
                </Box>
              </Reveal>
              <Reveal delay={140} style={{ height: "100%" }}>
                <Box className={classes.featureCard}>
                  <Box className={classes.featureCardContent}>
                    <Typography className={classes.featureCardLabel}>
                      Top Restaurants
                    </Typography>
                    <Typography className={classes.featureCardTitle}>
                      Best local restaurants
                    </Typography>
                    <Typography className={classes.featureCardDesc}>
                      Discover cuisines from hundreds of restaurants near you
                    </Typography>
                  </Box>
                  <Box className={classes.featureCardImage}>
                    <img src={FoodPlate} alt="Restaurant food" crossOrigin="anonymous" />
                  </Box>
                </Box>
              </Reveal>
            </Box>
          </Container>
        </Box>

        {/* ---- How It Works ---- */}
        <Box className={classes.section}>
          <SectionBackground3D variant="steps" />
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Reveal>
              <Typography className={classes.sectionTitle}>
                How RushmyFood Works
              </Typography>
            </Reveal>
            <Box className={classes.stepsGrid}>
              {[
                {
                  n: "1",
                  tilt: classes.stepCardA,
                  title: "Enter your address",
                  desc: "Tell us where you want your food delivered",
                },
                {
                  n: "2",
                  tilt: classes.stepCardB,
                  title: "Choose a restaurant",
                  desc: "Browse menus from top local restaurants",
                },
                {
                  n: "3",
                  tilt: classes.stepCardC,
                  title: "Get it delivered",
                  desc: "Track your order and enjoy your meal",
                },
              ].map((step, i) => (
                <Reveal key={step.n} delay={i * 130}>
                  <Box className={`${classes.stepCard} ${step.tilt}`}>
                    <Box className={classes.stepNumber}>{step.n}</Box>
                    <Typography className={classes.stepTitle}>
                      {step.title}
                    </Typography>
                    <Typography className={classes.stepDesc}>
                      {step.desc}
                    </Typography>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ---- Why Restaurants Love RushMyFood ---- */}
        <Box className={`${classes.section} ${classes.valueSection}`}>
          <SectionBackground3D variant="value" />
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Reveal>
              <Typography className={classes.sectionTitle}>
                Why Restaurants Love RushMyFood
              </Typography>
            </Reveal>
            <Box className={classes.valueGrid}>
              {[
                { img: ValueImgVariety, title: "Something for Everyone", desc: "We are a local online ordering and delivery company with an array of different cuisines to satisfy every taste bud." },
                { img: ValueImgDelivery, title: "Delivery or Pickup", desc: "We offer our restaurants delivery through our delivery partners all over the United States." },
                { img: ValueImgSave, title: "Save with RushMyFood", desc: "Get deals at all our restaurants and keep more of every order." },
              ].map((c, i) => (
                <Reveal key={c.title} delay={i * 140}>
                  <Box className={classes.valueCard}>
                    <Box
                      className={`${classes.valuePolaroid} ${
                        i % 2 ? classes.tiltRight : classes.tiltLeft
                      }`}
                    >
                      <img src={c.img} alt={c.title} crossOrigin="anonymous" />
                    </Box>
                    <Typography className={classes.valueTitle}>
                      {c.title}
                    </Typography>
                    <Typography className={classes.valueDesc}>
                      {c.desc}
                    </Typography>
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Container>
        </Box>

        {/* ---- Scrolling photo strip ---- */}
        <Box className={classes.photoMarquee}>
          <Box className={classes.photoTrack}>
            {[0, 1].map((dup) => (
              <Box component="span" key={dup} className={classes.photoGroup}>
                {STRIP_PHOTOS.map((src, i) => (
                  <Box key={`${dup}-${i}`} className={classes.photoTile}>
                    <img src={src} alt="" crossOrigin="anonymous" />
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>

        {/* ---- About / Credibility (dark band) ---- */}
        <Box className={`${classes.section} ${classes.aboutSection}`}>
          <SectionBackground3D variant="about" />
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Box className={classes.aboutGrid}>
              <Box className={classes.aboutContent}>
                <Reveal>
                  <Typography className={classes.aboutTitle}>
                    Pickup or Delivery From Restaurants Near You
                  </Typography>
                </Reveal>
                <Reveal delay={120}>
                  <Typography className={classes.aboutDesc}>
                    RushMyFood is a well established online ordering service since 2010. Technology
                    has allowed us to expand our delivery footprint exponentially, and offer timely
                    deliveries in multiple states. Today, we are processing 1000's of orders per week!
                  </Typography>
                </Reveal>
                <Reveal delay={220}>
                  <Button
                    variant="outlined"
                    className={classes.aboutButton}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate("/restaurant-list")}
                  >
                    Learn More
                  </Button>
                </Reveal>
              </Box>
              <Reveal delay={150}>
                <Box className={classes.aboutPolaroid}>
                  <img
                    src={AboutImg}
                    alt="Restaurants near you"
                    crossOrigin="anonymous"
                  />
                </Box>
              </Reveal>
            </Box>
          </Container>
        </Box>

        {/* ---- Partner & Driver postcards ---- */}
        <Box className={classes.section}>
          <SectionBackground3D variant="postcards" />
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Box className={classes.postcardsGrid}>
              <Reveal style={{ height: "100%" }}>
                <Box className={classes.postcard}>
                  <Box className={classes.postcardImage}>
                    <img
                      src={RestaurantImg}
                      alt="Partner restaurant"
                      crossOrigin="anonymous"
                    />
                    <span className={classes.postcardStamp}>
                      For Restaurants
                    </span>
                  </Box>
                  <Box className={classes.postcardBody}>
                    <Typography className={classes.postcardTitle}>
                      Grow your business with RushmyFood
                    </Typography>
                    <Typography className={classes.postcardDesc}>
                      Partner with us to reach more customers, increase your sales, and grow your business.
                      Our platform helps you manage orders and deliver great food to hungry customers.
                    </Typography>
                    <Button
                      variant="contained"
                      className={classes.postcardButton}
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/partner-signup")}
                    >
                      Become a Partner
                    </Button>
                  </Box>
                </Box>
              </Reveal>
              <Reveal delay={140} style={{ height: "100%" }}>
                <Box className={classes.postcard}>
                  <Box className={classes.postcardImage}>
                    <img
                      src={DeliveryRider}
                      alt="Delivery driver"
                      crossOrigin="anonymous"
                    />
                    <span className={classes.postcardStamp}>Coming Soon</span>
                  </Box>
                  <Box className={classes.postcardBody}>
                    <Typography className={classes.postcardTitle}>
                      Deliver with RushmyFood
                    </Typography>
                    <Typography className={classes.postcardDesc}>
                      Be your own boss. Set your own schedule. Earn good money delivering food
                      in your city with the RushmyFood driver app.
                    </Typography>
                    <Button
                      variant="contained"
                      className={classes.postcardButton}
                      disabled
                    >
                      Coming Soon
                    </Button>
                  </Box>
                </Box>
              </Reveal>
            </Box>
          </Container>
        </Box>

        {/* ---- App Download ---- */}
        <Box className={`${classes.section} ${classes.appSection}`}>
          <SectionBackground3D variant="app" />
          <Container maxWidth="lg" className={classes.sectionInner}>
            <Box className={classes.appGrid}>
              <Box className={classes.appContent}>
                <Reveal>
                  <Typography className={classes.appTitle}>
                    Download Our App for Easy Ordering
                  </Typography>
                </Reveal>
                <Reveal delay={110}>
                  <Typography className={classes.appDesc}>
                    We offer an app and website to make it convenient for you to order from your
                    favorite restaurants!
                  </Typography>
                </Reveal>
                <Reveal delay={200}>
                  <Box className={classes.appBadges}>
                    <a
                      href="https://apps.apple.com/us/app/rushmyfood/id1573433295"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={BadgeAppStore}
                        alt="Download on the App Store"
                        className={classes.appBadge}
                      />
                    </a>
                    <a
                      href="https://play.google.com/store/apps/details?id=io.appbuild.rushmyfo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={BadgeGooglePlay}
                        alt="Get it on Google Play"
                        className={classes.appBadge}
                      />
                    </a>
                  </Box>
                </Reveal>
              </Box>
              <Reveal delay={150}>
                <Box className={classes.appArt}>
                  <Box className={classes.phoneFrame}>
                    <span className={classes.phoneIsland} />
                    <Box className={classes.phoneScreen}>
                      <img src={AppHomeScreen} alt="RushMyFood app" />
                      <Box className={classes.phoneAppOverlay}>
                        <Box className={classes.phoneAppTile}>
                          <RunningLogo />
                        </Box>
                        <span className={classes.phoneAppName}>
                          RushMyFood
                        </span>
                      </Box>
                    </Box>
                    <span className={classes.phoneGloss} />
                  </Box>
                </Box>
              </Reveal>
            </Box>
          </Container>
        </Box>

        {/* ---- CTA ---- */}
        <Box className={`${classes.section} ${classes.ctaSection}`}>
          <SectionBackground3D variant="cta" />
          <Container maxWidth="md" className={classes.sectionInner}>
            <Reveal>
              <Typography className={classes.ctaTitle}>
                Ready to order?
              </Typography>
            </Reveal>
            <Reveal delay={110}>
              <Typography className={classes.ctaSubtitle}>
                Enter your address to find the best restaurants near you
              </Typography>
            </Reveal>
            <Reveal delay={200}>
              <Button
                variant="contained"
                className={classes.ctaButton}
                onClick={() => navigate("/restaurant-list")}
              >
                Get Started
              </Button>
            </Reveal>
          </Container>
        </Box>

        {/* Footer */}
        <Box className={classes.footerContainer}>
          <Box className={classes.footerWrapper}>
            <Footer />
          </Box>
        </Box>
      </Box>
    </Sentry.ErrorBoundary>
  );
}

export default Home;